import {
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import "./page-transition.scss";

export interface PageTransitionWaveProps {
  children: ReactNode;
  originX: string;
  originY: string;
  durationMs?: number;
  onComplete: () => void;
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export function PageTransitionWave({
  children,
  originX,
  originY,
  durationMs = 2000,
  onComplete,
}: PageTransitionWaveProps) {
  const revealRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const el = revealRef.current;
    if (!el) return;

    let cancelled = false;

    const finish = () => {
      if (cancelled) return;
      el.classList.remove("is-revealing");
      el.classList.add("is-revealed");
      onCompleteRef.current();
    };

    const onEnd = (event: AnimationEvent) => {
      if (event.animationName !== "page-transition-reveal") return;
      el.removeEventListener("animationend", onEnd);
      finish();
    };

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion) {
      el.classList.add("is-revealed");
      finish();
      return;
    }

    (async () => {
      el.classList.remove("is-revealing", "is-revealed");
      await waitForPaint();
      if (cancelled) return;

      void el.offsetWidth;
      el.classList.add("is-revealing");
      el.addEventListener("animationend", onEnd);
    })();

    return () => {
      cancelled = true;
      el.removeEventListener("animationend", onEnd);
    };
  }, [originX, originY, durationMs]);

  const revealStyle = {
    "--page-transition-origin-x": originX,
    "--page-transition-origin-y": originY,
    "--page-transition-duration": `${durationMs}ms`,
  } as CSSProperties;

  return (
    <div className="page-transition">
      <div
        ref={revealRef}
        className="page-transition__reveal"
        style={revealStyle}
      >
        {children}
      </div>
    </div>
  );
}
